import React from 'react'
import { useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import BankAccountOption from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/BankAccountOption'
import {
  GetBankAccountInfo,
  GetBankAccountInfoDocument,
  GetBankAccountInfoVariables,
} from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'
import { useMXConnectionStatus } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/MXConnectionStatusProvider'
import { useSelectConnection } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/SelectConnection.gql'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import { useRemoveConnection } from './RemoveConnection.gql'
import styles from './styles.module.scss'

export interface ConnectedBankAccountProps {
  id: string
  name: string
  imageUrl: string
  lastFour: string
  isSelected: boolean
  externalConnectionGuid: string
}

const ConnectedBankAccount = ({
  id,
  name,
  imageUrl,
  lastFour,
  isSelected,
  externalConnectionGuid,
}: ConnectedBankAccountProps) => {
  const { appId } = useParams()

  const [getClassName] = useNameFormatter('ConnectedBankAccount')
  const showToast = useToast()
  const { track } = useWebTracking()

  const [removeConnection, { client, loading: isRemoveConnectionLoading }] = useRemoveConnection()
  const [selectConnection, { loading: isSelectConnectionLoading }] = useSelectConnection()
  const { isLoadingNewAccount, newBankAccountId } = useMXConnectionStatus()

  const onDeleteBankConnection = async () => {
    await removeConnection({
      variables: { externalConnectionGuid },
      onCompleted: () => {
        client.cache.updateQuery<GetBankAccountInfo, GetBankAccountInfoVariables>(
          {
            query: GetBankAccountInfoDocument,
            variables: { creditApplicationId: appId ?? '' },
          },
          (data) => {
            if (!data?.creditApplication?.financeInfo?.externalBankOptions) {
              return data
            }

            const externalBankOptions =
              data.creditApplication.financeInfo.externalBankOptions.filter((option) => !!option) || []

            return {
              ...data,
              creditApplication: {
                ...data.creditApplication,
                financeInfo: {
                  ...data.creditApplication?.financeInfo,
                  externalBankOptions: externalBankOptions.filter(
                    (option) => option.externalConnectionGuid !== externalConnectionGuid,
                  ),
                },
              },
            }
          },
        )
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully removed bank account connection'
            id='sputnik.ConnectedBankAccount__F5/2OT'
          />,
          { autoHideDelay: 3000 },
        )
      },
      onError: () => {
        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='Failed to remove bank connection'
            id='sputnik.ConnectedBankAccount__ZsJRSn'
          />,
          { className: 'fs-unmask' },
        )
      },
    })
  }

  const onSelectBankConnection = async () => {
    await selectConnection({
      variables: {
        creditApplicationId: appId ?? '',
        financeInfo: {
          selectedExternalId: id,
        },
      },
      onCompleted: () => {
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully selected {name} as primary'
            id='sputnik.ConnectedBankAccount__os2LJF'
            values={{
              name,
            }}
          />,
          {
            autoHideDelay: 3000,
          },
        )
      },
      onError: () => {
        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='Failed to select <span>{name}</span> as primary'
            id='sputnik.ConnectedBankAccount__OPuA27'
            values={{
              span: (chunks) => <span className='fs-mask'>{chunks}</span>,
              name,
            }}
          />,
          { className: 'fs-unmask' },
        )
      },
    })
  }

  const isStateChanging =
    isRemoveConnectionLoading || isSelectConnectionLoading || (isLoadingNewAccount && newBankAccountId === id)

  return (
    <BankAccountOption
      image={
        <img
          alt={name}
          className={styles[getClassName('icon')]}
          src={imageUrl}
        />
      }
      isSelected={isSelected}
      isStateChanging={isStateChanging}
      lastFour={lastFour}
      name={name}
      onDelete={() => {
        track('se_signup__application_financial_info_connect_manual_delete_bank__fired')
        onDeleteBankConnection()
      }}
      onSelected={onSelectBankConnection}
    />
  )
}

export default ConnectedBankAccount
